import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { MediaSummary, MyRating } from '@teaplus/shared';
import { api, errorMessage } from './api';
import { qk, useProfileId } from './queries';
import { sk, useCanSocialise } from './social';

type Snap = Pick<MediaSummary, 'id' | 'type' | 'title' | 'posterUrl' | 'backdropUrl' | 'year'>;

const idsOf = (rows: MyRating[]) => new Set(rows.map((r) => r.mediaId));

/** The heart: titles someone loved, kept with their rating so stars and notes stay untouched. */
export function useFavouriteToggle() {
  const qc = useQueryClient();
  const p = useProfileId();
  const social = useCanSocialise();
  const ids = useQuery({
    queryKey: sk.ratings('favorites'),
    queryFn: () => api<MyRating[]>('/api/ratings?filter=favorites'),
    enabled: !!p,
    staleTime: 60_000,
    select: idsOf,
  }).data;
  const m = useMutation({
    mutationFn: async ({ media, add }: { media: Snap; add: boolean }) => {
      const { rating } = await api<{ rating: MyRating | null }>(`/api/ratings/${media.id}`);
      const saved = await api<{ rating: MyRating | null }>(`/api/ratings/${media.id}`, {
        method: 'PUT',
        body: {
          contentType: media.type,
          stars: rating?.stars ?? null,
          note: rating?.note ?? null,
          favorite: add,
          // Like ratings, always shared with friends.
          shared: social,
          snapshot: {
            title: media.title,
            posterUrl: media.posterUrl,
            backdropUrl: media.backdropUrl,
            year: media.year,
          },
        },
      });
      qc.setQueryData(sk.rating(media.id), saved);
    },
    onMutate: async ({ media, add }) => {
      await qc.cancelQueries({ queryKey: sk.ratings('favorites') });
      const prev = qc.getQueryData<MyRating[]>(sk.ratings('favorites'));
      const rest = (prev ?? []).filter((r) => r.mediaId !== media.id);
      qc.setQueryData<MyRating[]>(
        sk.ratings('favorites'),
        add
          ? [
              {
                mediaId: media.id,
                contentType: media.type,
                stars: null,
                note: null,
                favorite: true,
                shared: false,
                snapshot: null,
                updatedAt: new Date().toISOString(),
              },
              ...rest,
            ]
          : rest,
      );
      return { prev };
    },
    onError: (e, _v, c) => {
      qc.setQueryData(sk.ratings('favorites'), c?.prev);
      toast.error(errorMessage(e));
    },
    onSuccess: (_d, { media, add }) => {
      toast.success(
        add ? `Added ${media.title} to Favourites` : `Removed ${media.title} from Favourites`,
      );
      qc.invalidateQueries({ queryKey: ['ratings'] });
      qc.invalidateQueries({ queryKey: qk.home(p) });
    },
  });
  return {
    has: (id: string) => ids?.has(id) ?? false,
    toggle: (media: Snap) => m.mutate({ media, add: !(ids?.has(media.id) ?? false) }),
  };
}
